import { ChangeEvent, useEffect, useRef, useState } from 'react';
import { InputBase } from '@mui/material';
import { Search as SearchIcon, Close as ClearIcon } from '@mui/icons-material';
import { Tooltip } from '../tooltip';
import { getTextValidCharacters } from '../../../utils/common-utils';
import './styles.scss';
import { Button } from '../button';
import SelectMenu from '../select-menu';
import l from '../../../lang';
import Loading from '../loading';
interface InputSearchProps {
  value?: string;
  placeholder?: string;
  isLoading?: boolean;
  helpText?: string;
  onSearch?: (type: string, value: string) => void;
  searchOptions: { value: string; label: string }[];
}

const InputSearch = ({
  value,
  placeholder,
  isLoading = false,
  helpText = '',
  onSearch = () => {},
  searchOptions,
}: InputSearchProps) => {
  const defaultSearchByFilter = searchOptions[0];
  const [text, setText] = useState<string>('');
  const [lastSearch, setLastSearch] = useState<string | undefined>(undefined);
  const [isOpenHelp, setIsOpenHelp] = useState(false);
  const [searchBy, setSearchByFilter] = useState(defaultSearchByFilter.value);
  const searchRef = useRef(null);
  const prevSearchByRef = useRef(searchBy);

  useEffect(() => {
    if (searchBy !== prevSearchByRef.current) {
      setLastSearch(undefined);
      prevSearchByRef.current = searchBy;
    }
  }, [searchBy]);

  useEffect(() => {
    if (value === '') {
      setText('');
      setLastSearch(undefined);
    }
  }, [value]);

  const onSearchHandler = () => {
    if (isLoading || text === '') {
      return;
    }
    onSearch(searchBy, text);
    setLastSearch(text);
    setIsOpenHelp(false);
  };

  const onClearHandler = () => {
    if (isLoading) return;
    setLastSearch(undefined);
    setText('');
    onSearch(searchBy, '');
  };

  const onKeyDownHandler = (e: any) => {
    if (e.key === 'Enter') {
      onSearchHandler();
    }
  };

  const onTextChangeHandler = (e: ChangeEvent<HTMLInputElement>) => setText(getTextValidCharacters(e.currentTarget.value));

  const ButtonSearch = () => (
    <Button variant="contained" onMouseDown={onSearchHandler} disableElevation>
      <SearchIcon />
    </Button>
  );
  const ButtonClear = () => (
    <Button variant="contained" onMouseDown={onClearHandler} disableElevation>
      <ClearIcon />
    </Button>
  );

  const ButtonLoading = () => (
    <Button variant="contained" disableElevation>
      <div>...</div>
    </Button>
  );
  const ButtonAction = () =>
    isLoading ? <ButtonLoading /> : text.length > 0 && text === lastSearch ? <ButtonClear /> : <ButtonSearch />;

  return (
    <Tooltip
      PopperProps={{
        disablePortal: true,
      }}
      classes={{
        tooltip: 'input_search__tooltip',
        arrow: 'input_search__tooltip__arrow',
      }}
      open={isOpenHelp}
      disableFocusListener
      disableHoverListener
      disableTouchListener
      title={helpText}
    >
      <div ref={searchRef} className="input_search">
        <SelectMenu
          name="searchBy"
          defaultOptionEnabled={true}
          className="input_search__select"
          value={searchBy}
          optionsArray={searchOptions}
          onChange={e => {
            setSearchByFilter(e.target.value as string);
          }}
          input={<InputBase />}
        />
        <InputBase
          placeholder={placeholder}
          value={text}
          onChange={onTextChangeHandler}
          onKeyDown={onKeyDownHandler}
          onFocus={() => setIsOpenHelp(text.length === 0)}
          onBlur={() => setTimeout(() => setIsOpenHelp(false), 100)}
          className="input_search__input"
        />
        <ButtonAction />
      </div>
    </Tooltip>
  );
};

export default InputSearch;
